import { supabase } from './supabaseClient';

const BASE44_FIELD_TO_PG = {
  created_date: 'created_at',
  updated_date: 'updated_at',
};

function translateField(field) {
  return BASE44_FIELD_TO_PG[field] || field;
}

function applyOrder(query, sort) {
  if (!sort) return query;
  const desc = sort.startsWith('-');
  const field = translateField(desc ? sort.slice(1) : sort);
  return query.order(field, { ascending: !desc });
}

function applyFilters(query, filters) {
  if (!filters) return query;
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    if (value === null) {
      query = query.is(key, null);
    } else if (Array.isArray(value)) {
      query = query.in(key, value);
    } else {
      query = query.eq(key, value);
    }
  }
  return query;
}

function entity(table) {
  return {
    table,
    from: () => supabase.from(table),

    async list(sort, limit) {
      let q = supabase.from(table).select('*');
      q = applyOrder(q, sort);
      if (limit) q = q.limit(limit);
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },

    async filter(filters, sort, limit, offset) {
      let q = supabase.from(table).select('*');
      q = applyFilters(q, filters);
      q = applyOrder(q, sort);
      if (offset != null && limit) {
        q = q.range(offset, offset + limit - 1);
      } else if (limit) {
        q = q.limit(limit);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },

    async get(id) {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },

    async create(payload) {
      const { data, error } = await supabase
        .from(table)
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async bulkCreate(rows, { onConflict } = {}) {
      if (!Array.isArray(rows) || rows.length === 0) return [];
      const q = onConflict
        ? supabase.from(table).upsert(rows, { onConflict })
        : supabase.from(table).insert(rows);
      const { data, error } = await q.select();
      if (error) throw error;
      return data || [];
    },

    async update(id, payload) {
      const { data, error } = await supabase
        .from(table)
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },

    async delete(id) {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      return { ok: true };
    },

    async deleteWhere(filters) {
      let q = supabase.from(table).delete();
      q = applyFilters(q, filters);
      const { error } = await q;
      if (error) throw error;
      return { ok: true };
    },
  };
}

export const entities = {
  AppConfig:             entity('app_config'),
  CalendarEvent:         entity('calendar_events'),
  CalendarSync:          entity('sales_calendars'),
  CampaignCost:          entity('campaign_costs'),
  // Renouvellement (v2): n'utilise plus base44.entities. Accès via
  // /api/list-renouv-clients et /api/get-renouv (auth + audit log côté serveur).
  EtpMonthly:            entity('etp_monthly'),
  GRCSnapshot:           entity('grc_snapshots'),
  // Dernier snapshot par (source, période) : 238 lignes au lieu de 1 000, calculé en base.
  GRCSnapshotDernier:    entity('grc_snapshots_derniers'),
  LeadUnified:           entity('lead_unified'),
  SalesCalendar:         entity('sales_calendars'),
  SalesRep:              entity('sales_reps'),
  SyncStatus:            entity('sync_status'),
};

export default entities;
